import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, X, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ReviewAlertsBanner() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    try {
      const r = await api.get("/reviews/alerts", { params: { unread_only: true } });
      setAlerts(r.data || []);
    } catch (err) {
      console.error("Failed to fetch review alerts:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const markRead = useCallback(async (id) => {
    try {
      await api.post(`/reviews/alerts/${id}/mark-read`);
      setAlerts((a) => a.filter((x) => x.id !== id));
      toast.success("Marked as read");
    } catch (err) {
      console.error("Mark-read failed:", err);
      toast.error("Could not mark read");
    }
  }, []);

  if (loading || alerts.length === 0) return null;

  return (
    <div className="mb-6 space-y-3" data-testid="review-alerts-banner">
      {alerts.map((a) => (
        <div key={a.id} className="rounded-xl border-2 border-red-200 bg-red-50 p-4 flex items-start gap-3 shadow-sm">
          <div className="w-10 h-10 rounded-lg bg-red-500 text-white grid place-items-center flex-none">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-red-900">{a.rating}★ review from {a.author}</span>
              <span className="text-xs text-red-700">• Reply in 24h to protect rating</span>
            </div>
            <p className="text-sm text-slate-700 mt-1 line-clamp-2">"{a.text}"</p>
            <div className="mt-2 flex gap-2 flex-wrap">
              {a.google_maps_uri && (
                <a href={a.google_maps_uri} target="_blank" rel="noreferrer noopener"
                   className="text-xs font-semibold text-[#1E3A8A] hover:text-[#F97316] inline-flex items-center gap-1"
                   data-testid={`alert-reply-link-${a.id}`}>
                  Reply on Google Maps <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <button onClick={() => markRead(a.id)}
                      className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                      data-testid={`alert-dismiss-${a.id}`}>
                <X className="w-3 h-3" /> Mark read
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
